<template>
  <div class="bg-white dark:bg-dark-800 rounded-xl border border-gray-100 dark:border-dark-700">
    <div class="p-6">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-8">
        <p class="text-error-500 dark:text-error-400">{{ error }}</p>
      </div>

      <!-- Payment Details -->
      <div v-else-if="payment" class="space-y-4">
        <!-- Payment Information -->
        <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100">Payment Information</h3>
            <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="{
              'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300': payment.status === 'pending',
              'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300': payment.status === 'approved' || payment.status === 'completed',
              'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300': payment.status === 'rejected'
            }">
              {{ payment.status }}
            </span>
          </div>

          <div class="grid grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Amount</p>
              <p class="text-xl font-bold text-surface-900 dark:text-surface-100">₱{{ formatPrice(payment.amount) }}</p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Method</p>
              <p class="text-surface-900 dark:text-surface-100 capitalize">{{ payment.payment_method || 'N/A' }}</p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Reference</p>
              <p class="text-surface-900 dark:text-surface-100 text-sm break-all">{{ payment.transaction_reference ||
                'N/A' }}</p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Type</p>
              <p class="text-surface-900 dark:text-surface-100">{{ formatPaymentType(payment.payment_type) || 'N/A' }}
              </p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Created By</p>
              <p class="text-surface-900 dark:text-surface-100"
                :title="payment.created_by_name || payment.created_by || '-'">{{ payment.created_by_name ||
                  payment.created_by || '-' }}</p>
            </div>
          </div>

          <!-- Approval/Rejection Info -->
          <div v-if="payment.status === 'approved' || payment.status === 'completed'"
            class="p-3 bg-success-50 dark:bg-success-900/30 rounded border border-success-200 dark:border-success-800">
            <div class="flex items-center text-sm text-success-700 dark:text-success-300">
              <i class="fas fa-check-circle mr-2"></i>
              <span>Approved on {{ formatDateTime(payment.approved_at) }}</span>
              <span v-if="payment.approved_by_name" class="ml-2 text-xs text-surface-600 dark:text-surface-400">by {{
                payment.approved_by_name }}</span>
            </div>
          </div>

          <div v-if="payment.status === 'rejected'"
            class="p-3 bg-error-50 dark:bg-error-900/30 rounded border border-error-200 dark:border-error-800">
            <div class="text-sm text-error-700 dark:text-error-300">
              <div class="flex items-center">
                <i class="fas fa-times-circle mr-2"></i>
                <span class="font-medium">{{ payment.rejection_reason || 'No reason provided' }}</span>
              </div>
              <div v-if="payment.rejected_at || payment.rejected_by_name"
                class="mt-1 text-xs text-surface-600 dark:text-surface-400">
                <span v-if="payment.rejected_at">Rejected on {{ formatDateTime(payment.rejected_at) }}</span>
                <span v-if="payment.rejected_by_name" class="ml-2">by {{ payment.rejected_by_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Information -->
        <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3">Order Information</h3>
          <div class="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Order Number</p>
              <p class="text-surface-900 dark:text-surface-100 font-medium">{{ payment.order_number || (payment.order &&
                payment.order.order_number) || 'N/A' }}</p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Client</p>
              <p class="text-surface-900 dark:text-surface-100">{{ clientName || 'N/A' }}</p>
            </div>
            <div>
              <p class="font-medium text-surface-500 dark:text-surface-400">Payment Date</p>
              <p class="text-surface-900 dark:text-surface-100">{{ formatDateTime(payment.payment_date) || 'N/A' }}</p>
            </div>
          </div>
        </div>

        <!-- Payment Proof & Actions -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Payment Proof -->
          <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-4">
            <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3">Payment Proof</h3>
            <div
              class="relative h-32 bg-surface-50 dark:bg-surface-700/50 rounded-lg border border-surface-200 dark:border-surface-600 overflow-hidden">
              <img v-if="payment.image_url" :src="payment.image_url" alt="Payment Proof"
                class="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                @click="openImagePreview(payment.image_url)" @error="handleImageError" @load="handleImageLoad" />
              <div v-else class="flex items-center justify-center h-full">
                <div class="text-center">
                  <i class="fas fa-image text-surface-400 dark:text-surface-500 text-xl mb-1"></i>
                  <p class="text-xs text-surface-500 dark:text-surface-400">No proof available</p>
                </div>
              </div>
              <button v-if="payment.image_url" @click="openImagePreview(payment.image_url)"
                class="absolute bottom-1 right-1 bg-surface-0 bg-opacity-90 p-1.5 rounded-full shadow-md hover:bg-opacity-100 transition-all"
                title="View full image">
                <i class="fas fa-expand text-surface-600 text-xs"></i>
              </button>
            </div>
          </div>

          <!-- Actions -->
          <div class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-4">
            <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-3">Actions</h3>
            <button @click="showReceiptModal"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm font-medium"
              title="Print Receipt">
              <i class="fas fa-print"></i>
              Print Receipt
            </button>
          </div>
        </div>

        <!-- Payment Notes -->
        <div v-if="payment.notes"
          class="bg-white dark:bg-dark-800 rounded-lg border border-surface-200 dark:border-surface-700 p-4">
          <h3 class="text-base font-semibold text-surface-900 dark:text-surface-100 mb-2">Notes</h3>
          <p class="text-sm text-surface-600 dark:text-surface-300 whitespace-pre-wrap">{{ payment.notes }}</p>
        </div>

        <!-- Approval Actions (only shown when opened from approval context) -->
        <div v-if="isApprovalContext && payment && payment.status === 'pending' && hasPermission('payments.approve')"
          class="mt-6 pt-6 border-t border-surface-200 dark:border-surface-600">
          <div class="flex flex-col sm:flex-row gap-3">
            <button @click="handleApprove" :disabled="isProcessingApproval"
              class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-success-600 text-white text-sm font-medium rounded-lg hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-success-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ isProcessingApproval ? 'Approving...' : 'Approve Payment' }}
            </button>
            <button @click="handleReject" :disabled="isProcessingApproval"
              class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-error-600 text-white text-sm font-medium rounded-lg hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-error-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-times"></i>
              {{ isProcessingApproval ? 'Processing...' : 'Reject Payment' }}
            </button>
          </div>
        </div>
      </div>

      <!-- No Data State -->
      <div v-else class="text-center py-8">
        <p class="text-surface-500 dark:text-surface-400">No payment details available</p>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <Modal v-model="showImagePreview" size="xl">
      <template #title>Payment Proof Preview</template>
      <template #content>
        <div class="relative p-6">
          <img :src="selectedImage" alt="Payment Proof" class="w-full h-auto rounded-lg" />
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button @click="showImagePreview = false"
            class="px-4 py-2 text-surface-700 dark:text-surface-200 border border-surface-300 dark:border-surface-600 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-700">
            Close
          </button>
        </div>
      </template>
    </Modal>

    <!-- Receipt Modal -->
    <Modal v-model="showReceiptModalFlag" size="lg">
      <template #title>
        Payment Receipt
      </template>
      <template #content>
        <PaymentReceipt v-if="selectedPaymentForReceipt" :payment="selectedPaymentForReceipt" />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { useAuth } from '@/composables/useAuth';
import { useUserEmployeeData } from '@/composables/useMasterData';
import Modal from '@/components/common/Modal.vue';
import PaymentReceipt from '@/components/payments/PaymentReceipt.vue';

const props = defineProps({
  paymentId: {
    type: [String, Number],
    required: true
  },
  isApprovalContext: {
    type: Boolean,
    default: false
  }
});

const api = useApi();
const toast = useToast();
const { user, hasPermission } = useAuth();
const { getUserName } = useUserEmployeeData();

const payment = ref(null);
const loading = ref(true);
const error = ref(null);
const showImagePreview = ref(false);
const selectedImage = ref(null);
const showReceiptModalFlag = ref(false);
const selectedPaymentForReceipt = ref(null);

// Approval state
const isProcessingApproval = ref(false);

const clientName = computed(() => {
  if (payment.value) {
    // Check for client_name directly in payment object
    if (payment.value.client_name && payment.value.client_name !== 'No Client' && payment.value.client_name !== 'N/A') {
      return payment.value.client_name;
    }

    // Try client data in order object
    if (payment.value.order) {
      if (payment.value.order.client_name && payment.value.order.client_name !== 'No Client') {
        return payment.value.order.client_name;
      }

      // Try to construct from client fields in order
      if (payment.value.order.client) {
        const client = payment.value.order.client;
        if (client.company_name && client.company_name.trim() !== '') {
          return client.company_name;
        }

        if (client.first_name || client.last_name) {
          return `${client.first_name || ''} ${client.last_name || ''}`.trim();
        }
      }

      // Try first_name and last_name directly in order
      if (payment.value.order.first_name || payment.value.order.last_name) {
        return `${payment.value.order.first_name || ''} ${payment.value.order.last_name || ''}`.trim();
      }

      // Try company_name in order
      if (payment.value.order.company_name) {
        return payment.value.order.company_name;
      }
    }
  }

  return null;
});

// Watch clientName changes for debugging
watch(clientName, (newVal, oldVal) => {
  console.log('Client name computed value changed:', {
    from: oldVal,
    to: newVal,
    payment: payment.value ? {
      client_name: payment.value.client_name,
      has_order: !!payment.value.order,
      order_client_name: payment.value.order?.client_name,
      has_client: !!(payment.value.order?.client),
      client_details: payment.value.order?.client
    } : null
  });
});

const getImageUrl = (payment) => {
  if (!payment || !payment.image_url) return null;

  // If it's already an absolute URL (including R2 URLs), return as is
  if (payment.image_url.startsWith('http://') || payment.image_url.startsWith('https://')) {
    return payment.image_url;
  }

  // Clean up the path (remove any leading slashes)
  const cleanPath = payment.image_url.replace(/^\/+/, '');

  // Get the base URL from environment variables
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    console.error('VITE_API_URL environment variable is not set');
    return null;
  }

  // Construct and return the full URL
  return `${apiUrl}/${cleanPath}`;
};

const openImagePreview = (imageUrl) => {
  selectedImage.value = imageUrl;
  showImagePreview.value = true;
};

const loadPaymentDetails = async () => {
  try {
    loading.value = true;
    error.value = null;
    console.log('Loading payment details for ID:', props.paymentId);

    const response = await api.get(`/payments/${props.paymentId}`);
    if (response.data) {
      console.log('Raw payment data:', response.data);
      console.log('Order information in response:', {
        order_id: response.data.order_id,
        order_number: response.data.order_number,
        client_name: response.data.client_name,
        payment_type: response.data.payment_type,
        payment_type_formatted: formatPaymentType(response.data.payment_type)
      });

      // Process any needed fields from the response
      const paymentData = {
        ...response.data,
        image_url: getImageUrl(response.data)
      };

      // If we have order_id but missing order details, fetch them separately
      if (paymentData.order_id && (!paymentData.order_number || !paymentData.client_name || (paymentData.client_name === 'No Client'))) {
        try {
          console.log('Fetching additional order details for order ID:', paymentData.order_id);
          const orderResponse = await api.get(`/orders/${paymentData.order_id}`);
          if (orderResponse.data) {
            console.log('Order details:', orderResponse.data);
            // Add the order details to the payment data
            if (!paymentData.order) {
              paymentData.order = {};
            }
            paymentData.order = {
              ...paymentData.order,
              ...orderResponse.data
            };

            // If the main payment object is missing these fields, copy them from the order
            if (!paymentData.order_number && orderResponse.data.order_number) {
              paymentData.order_number = orderResponse.data.order_number;
            }

            // Update client_name if missing or generic
            if (!paymentData.client_name || paymentData.client_name === 'No Client' || paymentData.client_name === 'N/A') {
              // Try to get client name from order response
              if (orderResponse.data.client_name) {
                paymentData.client_name = orderResponse.data.client_name;
              } else if (orderResponse.data.client_id) {
                // If we have client_id but no client_name, try to fetch client details
                try {
                  const clientResponse = await api.get(`/clients/${orderResponse.data.client_id}`);
                  if (clientResponse.data) {
                    console.log('Client details:', clientResponse.data);
                    if (!paymentData.order.client) {
                      paymentData.order.client = {};
                    }
                    paymentData.order.client = clientResponse.data;

                    // Set client_name based on client data
                    if (clientResponse.data.company_name && clientResponse.data.company_name.trim() !== '') {
                      paymentData.client_name = clientResponse.data.company_name;
                    } else if (clientResponse.data.first_name || clientResponse.data.last_name) {
                      paymentData.client_name = `${clientResponse.data.first_name || ''} ${clientResponse.data.last_name || ''}`.trim();
                    }
                  }
                } catch (clientError) {
                  console.error('Failed to fetch client details:', clientError);
                }
              }
            }
          }
        } catch (orderError) {
          console.error('Failed to fetch order details:', orderError);
        }
      }

      // Fetch user information if we have user IDs but no names
      if ((response.data.status === 'rejected' && response.data.rejected_by && !paymentData.rejected_by_name) ||
        ((response.data.status === 'approved' || response.data.status === 'completed') &&
          response.data.approved_by && !paymentData.approved_by_name)) {

        try {
          // Fetch user names using cached data
          if (response.data.status === 'rejected' && response.data.rejected_by && !paymentData.rejected_by_name) {
            const rejectedByName = await fetchUserName(response.data.rejected_by);
            if (rejectedByName) {
              paymentData.rejected_by_name = rejectedByName;
              console.log(`[PaymentPreview] Set rejected_by_name to: ${rejectedByName}`);
            }
          }

          if ((response.data.status === 'approved' || response.data.status === 'completed') &&
            response.data.approved_by && !paymentData.approved_by_name) {
            const approvedByName = await fetchUserName(response.data.approved_by);
            if (approvedByName) {
              paymentData.approved_by_name = approvedByName;
              console.log(`[PaymentPreview] Set approved_by_name to: ${approvedByName}`);
            }
          }
        } catch (userError) {
          console.error('[PaymentPreview] Error fetching user information:', userError);
        }
      }

      // Log payment status information
      if (response.data.status === 'rejected') {
        console.log('Payment was rejected:', {
          rejection_reason: response.data.rejection_reason || 'No reason provided',
          rejected_at: response.data.rejected_at || 'No timestamp available',
          rejected_by: response.data.rejected_by || 'Unknown',
          rejected_by_name: paymentData.rejected_by_name || 'Unknown'
        });
      } else if (response.data.status === 'approved' || response.data.status === 'completed') {
        console.log('Payment was approved:', {
          approved_at: response.data.approved_at || 'No timestamp available',
          approved_by: response.data.approved_by || 'Unknown',
          approved_by_name: paymentData.approved_by_name || 'Unknown'
        });
      }

      // Ensure creator name is present if creator ID exists
      if (paymentData.created_by && !paymentData.created_by_name) {
        try {
          const creatorName = await fetchUserName(paymentData.created_by);
          if (creatorName) {
            paymentData.created_by_name = creatorName;
            console.log(`[PaymentPreview] Set created_by_name to: ${creatorName}`);
          }
        } catch (creatorErr) {
          console.error('[PaymentPreview] Failed to fetch creator name:', creatorErr);
        }
      }

      payment.value = paymentData;
      console.log('Processed payment data:', payment.value);
      console.log('Client name calculation:', {
        direct_client_name: payment.value.client_name,
        order_client_name: payment.value.order?.client_name,
        computed_client_name: clientName.value,
        client_in_order: payment.value.order?.client
      });
    }
  } catch (err) {
    console.error('Error loading payment:', err);
    error.value = 'Failed to load payment details';
    toast.error('Failed to load payment details');
  } finally {
    loading.value = false;
  }
};

const formatPrice = (price) => {
  return Number(price).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatDateTime = (datetime) => {
  if (!datetime) return '';
  return new Date(datetime).toLocaleString('en-PH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPaymentType = (type) => {
  if (!type) return '';

  // If it's already a descriptive string, return it
  if (typeof type === 'string' && !type.match(/^\d+$/)) {
    return type;
  }

  // If it's a number or numeric string, map it to a name
  const paymentTypes = {
    '1': 'Collection',
    '2': 'Downpayment',
    '3': 'Fully Paid',
    '4': 'Balance'
  };

  return paymentTypes[type] || type;
};

const handleImageError = (event) => {
  console.error('Failed to load image:', event.target.src);
  event.target.classList.add('hidden');
  event.target.parentElement.innerHTML = `
    <div class="flex items-center justify-center h-48 bg-surface-50 rounded-lg">
      <div class="text-center">
        <i class="fas fa-exclamation-circle text-surface-400 text-3xl mb-2"></i>
        <p class="text-sm text-surface-500">Failed to load payment proof</p>
      </div>
    </div>
  `;
};

const handleImageLoad = (event) => {
  event.target.classList.remove('hidden');
};

// Helper function to fetch a specific user's name using cached data
const fetchUserName = async (userId) => {
  if (!userId) return null;

  try {
    console.log(`[PaymentPreview] Fetching name for user ID: ${userId} from cache`);
    const userName = await getUserName(userId);
    console.log(`[PaymentPreview] Retrieved user name from cache: ${userName}`);
    return userName || 'Unknown User';
  } catch (error) {
    console.error(`[PaymentPreview] Error fetching user ${userId} from cache:`, error);
    return 'Unknown User';
  }
};

// Function to show receipt modal
// Approval handlers
const handleApprove = async () => {
  if (!payment.value || isProcessingApproval.value || payment.value.status !== 'pending') return;

  try {
    isProcessingApproval.value = true;

    const response = await api.post(`/payments/${payment.value.id}/approve`, {
      status: 'approved',
      approved_by: 'current_user' // This should be handled by the backend
    });

    if (response.data) {
      //toast.success('Payment approved successfully');

      // Refresh the payment data to show updated status
      await loadPaymentDetails();

      // Emit event to parent component to refresh the approvals list
      emit('approved', payment.value);
    }
  } catch (error) {
    console.error('Error approving payment:', error);
    toast.error(error.response?.data?.message || 'Failed to approve payment');
  } finally {
    isProcessingApproval.value = false;
  }
};

const handleReject = async () => {
  if (!payment.value || isProcessingApproval.value || payment.value.status !== 'pending') return;

  const reason = prompt('Please provide a reason for rejecting this payment:');
  if (!reason || reason.trim() === '') {
    toast.warning('Please provide a reason for rejection');
    return;
  }

  try {
    isProcessingApproval.value = true;

    const response = await api.post(`/payments/${payment.value.id}/reject`, {
      status: 'rejected',
      rejection_reason: reason.trim(),
      rejected_by: 'current_user' // This should be handled by the backend
    });

    if (response.data) {
      toast.success('Payment rejected successfully');

      // Refresh the payment data to show updated status
      await loadPaymentDetails();

      // Emit event to parent component to refresh the approvals list
      emit('rejected', payment.value);
    }
  } catch (error) {
    console.error('Error rejecting payment:', error);
    toast.error(error.response?.data?.message || 'Failed to reject payment');
  } finally {
    isProcessingApproval.value = false;
  }
};

// Define emits for approval events
const emit = defineEmits(['approved', 'rejected']);

const showReceiptModal = async () => {
  try {
    console.log('=== RECEIPT MODAL DEBUG ===');
    console.log('Payment data before fetch:', {
      id: payment.value.id,
      order_number: payment.value.order_number,
      amount: payment.value.amount,
      payment_method: payment.value.payment_method
    });

    let paymentDetails;

    try {
      // Get payment details with order items included
      const response = await api.get(`/payments/${payment.value.id}`, {
        params: {
          include: ['order.items']
        }
      });
      paymentDetails = response.data;

      console.log('Payment details response:', {
        payment_id: paymentDetails.id,
        order: paymentDetails.order ? {
          id: paymentDetails.order.id,
          items: paymentDetails.order.items?.length || 0,
          has_old_products: !!paymentDetails.order.old_products
        } : 'No order data'
      });

      // If no items were returned, try fetching them separately
      if (!paymentDetails.order?.items || paymentDetails.order.items.length === 0) {
        try {
          const itemsResponse = await api.get(`/orders/${paymentDetails.order_id}/items`);
          if (itemsResponse.data && Array.isArray(itemsResponse.data)) {
            console.log('Fetched order items:', itemsResponse.data);
            paymentDetails.order.items = itemsResponse.data.map(item => ({
              ...item,
              product_name: item.product_name || 'Unknown Product',
              quantity: Number(item.quantity) || 0,
              unit_price: Number(item.unit_price) || 0
            }));
          }
        } catch (itemsError) {
          console.error('Failed to fetch order items:', itemsError);
          paymentDetails.order.items = [];
        }
      }

      // If payment has order_id, fetch order details separately
      if (paymentDetails.order_id) {
        try {
          // Fetch basic order details first
          const orderResponse = await api.get(`/orders/${paymentDetails.order_id}`);
          paymentDetails.order = orderResponse.data;

          // Then fetch order items separately
          try {
            const itemsResponse = await api.get(`/orders/${paymentDetails.order_id}/items`);
            if (itemsResponse.data && Array.isArray(itemsResponse.data)) {
              console.log('Fetched order items:', itemsResponse.data);
              paymentDetails.order.items = itemsResponse.data.map(item => ({
                ...item,
                product_name: item.product_name || 'Unknown Product',
                quantity: Number(item.quantity) || 0,
                unit_price: Number(item.unit_price) || 0
              }));
            } else {
              console.log('No items found or invalid response:', itemsResponse.data);
              paymentDetails.order.items = [];
            }
          } catch (itemsError) {
            console.error('Failed to fetch order items:', itemsError);
            paymentDetails.order.items = [];
          }

          // If order has old_orderid, fetch old products
          if (paymentDetails.order?.old_orderid) {
            try {
              const oldProductsResponse = await api.get(`/orders/${paymentDetails.order_id}/old-products`);
              if (oldProductsResponse.data) {
                paymentDetails.order.old_products = oldProductsResponse.data;
              }
            } catch (oldProductsError) {
              console.error('Failed to fetch old products:', oldProductsError);
              paymentDetails.order.old_products = null;
            }
          }
        } catch (orderError) {
          console.error('Failed to fetch order details:', orderError);
          paymentDetails.order = null;
        }
      }
    } catch (error) {
      console.error('Payment fetch failed:', error);

      // Use available payment data as fallback
      paymentDetails = {
        ...payment.value,
        payment_date: payment.value.payment_date || payment.value.created_at || payment.value.date_created || new Date().toISOString(),
        status: payment.value.status || 'completed',
        payment_method: payment.value.payment_method || 'cash'
      };
    }

    // Ensure we have at least the basic payment information
    if (!paymentDetails) {
      paymentDetails = {
        ...payment.value,
        payment_date: payment.value.payment_date || payment.value.created_at || payment.value.date_created || new Date().toISOString(),
        status: payment.value.status || 'completed',
        payment_method: payment.value.payment_method || 'cash'
      };
    }

    // Use the current user as the cashier (person printing the receipt)
    // Removed console.logs for production cleanliness

    let currentUserName = 'SUPERVISOR';
    if (user.value) {
      // Try both camelCase and snake_case field names
      const firstName = (user.value.firstName || user.value.first_name || '').trim();
      const lastName = (user.value.lastName || user.value.last_name || '').trim();

      // Build full name, only adding space if both parts exist
      let fullName = '';
      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      } else if (firstName) {
        fullName = firstName;
      } else if (lastName) {
        fullName = lastName;
      }

      if (fullName) {
        currentUserName = fullName;
      } else if (user.value.email) {
        // Use email if no name is available
        currentUserName = user.value.email;
      }
    }

    selectedPaymentForReceipt.value = {
      ...paymentDetails,
      created_by_name: currentUserName
    };

    showReceiptModalFlag.value = true;
  } catch (error) {
    console.error('Error preparing receipt:', error);
    toast.error('Failed to prepare receipt');
  }
};

onMounted(() => {
  loadPaymentDetails();
});
</script>